import {FC, MouseEvent, useState} from 'react';
import {Link} from 'react-router-dom';
import {ContentCopyOutlined, PersonOutlineOutlined} from '@mui/icons-material';
import {Box, Button, IconButton, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import copy from 'copy-to-clipboard';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {phonePillsRowSx, phoneTitleCellSx} from 'src/components/mobile/mobileDesign.sx';
import {MissingBexioPill} from 'src/components/common/MissingBexioPill';
import {useMissingBexio} from 'src/hooks/useMissingBexio';
import {NotifyError, NotifySuccess} from '../../../../utils/notification';
import {usersApi} from '../../../../api';
import {useAppDispatch} from '../../../../app/hooks';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {AppDispatch, BaseUserInfo, UserDTO} from '../../../../types';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from '../../../../utils/helper';
import {
    factLinkSx,
    iconBtnSx,
    idValueSx,
    menuBtnSx,
    menuBtnTextSx,
    popoverSx,
} from './UserItemCard.sx';
import {ColorField} from 'src/components/common/ColorField';
import {UserTypeEnum} from 'src/enums';
import {UserCell} from 'src/components/users/UserCell';
import {EmployeeCell} from 'src/components/users/EmployeeCell';
import {TableCard} from '../../../tables/respnsiveTable/TableCard';
import {MenuButton} from '../../../tables/respnsiveTable/MenuButton';
import dayjs from 'dayjs';

type DeleteUserMutationTrigger = ReturnType<typeof usersApi.useDeleteUserMutation>[0];

const handleDeleteUser =
    (trigger: DeleteUserMutationTrigger, dispatch: AppDispatch, userId: number, businessId: number) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({userId, businessId})
                    .unwrap()
                    .finally(() => {
                        dispatch(preloaderChangeStatus(false));
                    });
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const UserItemCard: FC<BaseUserInfo> = (user) => {
    const {
        id,
        userType,
        firstName,
        lastName,
        contracts,
        emailAddress,
        business,
        location,
        created,
        canDeleteUser,
    } = user;
    const intl = useIntl();
    const [trigger] = usersApi.useDeleteUserMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const copyToClipboard = () => {
        copy(String(id), {debug: true});
        NotifySuccess(intl.formatMessage({id: 'copiedSuccesfully'}));
    };

    const displayName = `${firstName || ''} ${lastName || ''}`.trim() || emailAddress || '';

    // The name people aim a finger at is the door, and the door is this list's own name cell - the
    // same component the grid renders (UsersTable.tableConfig.tsx), so a row and its card cannot
    // open different pages. Which of the two cells is the grid's own choice as well: an employee is
    // a person inside a company, and opens the employee page rather than the user page.
    const titleCell =
        userType === UserTypeEnum.EMPLOYEE && user.employee ? (
            <EmployeeCell data={user as unknown as UserDTO}/>
        ) : (
            <UserCell data={user}/>
        );

    // The warning the row carries as a red name. The card's title is the canvas's dark identity line
    // instead, so the warning is said where the card says everything else that is true of the record.
    // One call covers both cells above: the rule reads the payment mode only for an employee.
    const showMissingBexio = useMissingBexio({
        hasBexio: !!user.bexioDataList?.length,
        userType,
        isCompany: userType === UserTypeEnum.COMPANY,
        paymentMode: user.employee?.consumptionPaymentMode,
    });

    // One node, drawn by whichever of the two the width calls for: the handler and the gate around
    // it are the same in both.
    const deleteAction = business ? (
        <Button
            sx={menuBtnSx}
            onClick={handleDeleteUser(trigger, dispatch, +id, +business.id)}
        >
            <Typography sx={menuBtnTextSx}>{intl.formatMessage({id: 'delete'})}</Typography>
        </Button>
    ) : null;

    // The menu the card's head carries. The sheet and the popover are portalled, so sitting in the
    // head's row costs the layout nothing.
    const menuAction = business ? (
        <>
            {canDeleteUser && <MenuButton handleClick={handleClick}/>}
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<PersonOutlineOutlined/>}
                    title={displayName}
                    subtitle={emailAddress}
                >
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {deleteAction}
                </Popover>
            )}
        </>
    ) : undefined;

    return (
        <TableCard>
            <PhoneCardHead
                icon={<PersonOutlineOutlined/>}
                person
                title={<Box sx={phoneTitleCellSx}>{titleCell}</Box>}
                subtitle={emailAddress}
                action={menuAction}
            />
            {(userType || contracts || showMissingBexio) && (
                <Box sx={phonePillsRowSx}>
                    {userType && <ColorField label={userType}/>}
                    {showMissingBexio && <MissingBexioPill/>}
                    {contracts &&
                        [...contracts]
                            ?.sort((a, b) => {
                                // Priority order based on backend statuses: active contracts first
                                const priorityOrder = {
                                    'OPERATION': 1,          // Active - highest priority
                                    'INSTALLATION': 2,       // Active setup
                                    'WAIT_FOR_SIGNATURE': 3, // Pending
                                    'ARCHIVED': 4            // Inactive - lowest priority
                                };

                                const aPriority = priorityOrder[a.status as keyof typeof priorityOrder] || 999;
                                const bPriority = priorityOrder[b.status as keyof typeof priorityOrder] || 999;

                                // If same priority, sort by creation date (newest first)
                                if (aPriority === bPriority) {
                                    return b.created - a.created;
                                }

                                return aPriority - bPriority;
                            })
                            ?.slice(0, 1)
                            ?.map((contract) => (
                                <ColorField
                                    key={contract.id}
                                    label={contract.status}
                                />
                            ))}
                </Box>
            )}
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'userId'}),
                        value: (
                            <Box
                                component="span"
                                sx={idValueSx}
                            >
                                {/* Plain text, not a link. It used to open /app/users/:id for
                                    everyone, which is the right page for only some of them; the
                                    title is now the card's one door, and a second link here
                                    could only disagree with it. */}
                                {id}
                                <IconButton
                                    onClick={copyToClipboard}
                                    sx={iconBtnSx}
                                >
                                    <ContentCopyOutlined sx={{width: '0.75rem'}}/>
                                </IconButton>
                            </Box>
                        ),
                    },
                    {
                        label: intl.formatMessage({id: 'dateColumnHeader'}),
                        value: dayjs(created).format('DD/MM/YY'),
                    },
                    {
                        label: intl.formatMessage({id: 'locationColumnHeader'}),
                        value: (
                            <Link
                                to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations, `${location?.id}`])}
                            >
                                <Typography
                                    component="span"
                                    sx={factLinkSx}
                                >
                                    {location?.name ?? '-'}
                                </Typography>
                            </Link>
                        ),
                    },
                ]}
            />
        </TableCard>
    );
};
